/**
 * Hooks de mutación de **usuarios** (alta, edición, contraseña, permisos, borrado).
 *
 * Cada mutación, en éxito, **invalida las queries afectadas** (`docs/
 * frontend_conventions.md`, Datos): la raíz `['users']` cubre la lista agrupada y
 * los detalles. El gating de permiso (`manage_users`) lo decide la UI; aquí solo
 * se ejecuta y se propaga el `ApiError` (la pantalla traduce `{error_code,
 * message}` con `teamErrorMessage`).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  changeUserPassword,
  createUser,
  deleteUser,
  resetUserPermissions,
  setUserPermissions,
  updateUser,
} from "../api";
import type {
  ChangePasswordBody,
  CreateUserBody,
  SetPermissionsBody,
  UpdateUserBody,
  UserDetail,
} from "../types";
import { USERS_ROOT_KEY } from "./queryKeys";

/** Invalida toda la rama de usuarios (lista agrupada + detalles). */
function useInvalidateUsers() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: USERS_ROOT_KEY });
  };
}

/** Alta de usuario (`POST /users`). */
export function useCreateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation<UserDetail, Error, CreateUserBody>({
    mutationFn: (body) => createUser(body),
    onSuccess: invalidate,
  });
}

/** Argumentos de edición: el username actual + los cambios. */
interface UpdateUserArgs {
  username: string;
  body: UpdateUserBody;
}

/** Edición de usuario (`PATCH /users/<username>`). */
export function useUpdateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation<UserDetail, Error, UpdateUserArgs>({
    mutationFn: ({ username, body }) => updateUser(username, body),
    onSuccess: invalidate,
  });
}

/** Argumentos de cambio de contraseña. */
interface ChangePasswordArgs {
  username: string;
  body: ChangePasswordBody;
}

/** Cambio de contraseña (`POST /users/<username>/password`). */
export function useChangePassword() {
  // No cambia la lista (ni permisos): no invalida nada, pero mantiene la firma
  // de mutación uniforme. El éxito lo gestiona la pantalla (cierra el modal).
  return useMutation<UserDetail, Error, ChangePasswordArgs>({
    mutationFn: ({ username, body }) => changeUserPassword(username, body),
  });
}

/** Argumentos de fijación de permisos. */
interface SetPermissionsArgs {
  username: string;
  body: SetPermissionsBody;
}

/** Fija el override de permisos de un usuario (`PATCH .../permissions`). */
export function useSetUserPermissions() {
  const invalidate = useInvalidateUsers();
  return useMutation<UserDetail, Error, SetPermissionsArgs>({
    mutationFn: ({ username, body }) => setUserPermissions(username, body),
    onSuccess: invalidate,
  });
}

/** Resetea el override de permisos de un usuario (`POST .../permissions/reset`). */
export function useResetUserPermissions() {
  const invalidate = useInvalidateUsers();
  return useMutation<UserDetail, Error, string>({
    mutationFn: (username) => resetUserPermissions(username),
    onSuccess: invalidate,
  });
}

/** Borrado de usuario (`DELETE /users/<username>`). */
export function useDeleteUser() {
  const invalidate = useInvalidateUsers();
  return useMutation<void, Error, string>({
    mutationFn: (username) => deleteUser(username),
    onSuccess: invalidate,
  });
}
